import { z } from 'zod';

/**
 * Central de ajuda (M18).
 *
 * O catálogo de artigos vive no **código**, e não no banco: ajuda é parte do
 * produto, versionada com ele. Quando uma tela muda, o artigo dela muda no
 * mesmo commit — e o vídeo é regravado pela mesma suíte que testa a tela.
 *
 * Nenhuma igreja escreve ajuda própria, e por isso não há tabela nem migração.
 */

export const AREAS_DE_AJUDA = [
  'primeiros-passos',
  'pessoas',
  'escalas',
  'comunicacao',
  'culto',
  'kids',
  'relatorios',
  'conta',
] as const;
export const areaDeAjudaSchema = z.enum(AREAS_DE_AJUDA);
export type AreaDeAjuda = z.infer<typeof areaDeAjudaSchema>;

export const AREA_LABEL: Record<AreaDeAjuda, string> = {
  'primeiros-passos': 'Primeiros passos',
  pessoas: 'Pessoas',
  escalas: 'Escalas',
  comunicacao: 'Avisos e WhatsApp',
  culto: 'Roteiro e repertório',
  kids: 'Ministério Infantil',
  relatorios: 'Relatórios',
  conta: 'Conta e assinatura',
};

export interface ArtigoDeAjuda {
  slug: string;
  area: AreaDeAjuda;
  /** O título é a pergunta que a pessoa faria, e não o nome da tela. */
  titulo: string;
  /** Uma frase: o que ela vai conseguir fazer depois de ler. */
  resumo: string;
  /** Os passos, em linguagem direta. Cada um é uma ação. */
  passos: readonly string[];
  /** O que costuma dar errado, e como resolver. */
  dicas?: readonly string[];
  /**
   * As telas em que o botão de ajuda leva a este artigo. É o que faz a ajuda
   * ser **contextual** em vez de uma lista que ninguém procura.
   */
  telas: readonly string[];
  /**
   * O tutorial em vídeo, quando existe. O nome do arquivo é o do roteiro em
   * `e2e/tutorials/` — a ajuda e a gravação combinam por convenção, e não por
   * cadastro (seção 13.4).
   */
  video?: string;
  /** Termos que a pessoa digitaria na busca e que não estão no título. */
  busca?: readonly string[];
}

/**
 * Os artigos do produto.
 *
 * Escritos como a igreja fala: "Como monto a escala do mês?", e não "Gestão de
 * escalas". Quem procura ajuda está com um problema, não estudando um módulo.
 */
export const ARTIGOS_DE_AJUDA: readonly ArtigoDeAjuda[] = [
  {
    slug: 'cadastrar-a-igreja',
    area: 'primeiros-passos',
    titulo: 'Acabei de criar a conta. Por onde começo?',
    resumo: 'Deixa a igreja pronta para montar a primeira escala em quinze minutos.',
    passos: [
      'Cadastre as pessoas que servem — se você já tem uma planilha, importe: leva menos de um minuto.',
      'Monte os departamentos e as funções com o botão "Usar a estrutura pronta" e ajuste o que for diferente na sua igreja.',
      'Diga quem faz o quê: em cada pessoa, marque as funções que ela sabe fazer.',
      'Crie o culto de domingo como evento que se repete.',
      'Monte a escala e publique. É a publicação que avisa quem foi escalado.',
    ],
    dicas: [
      'O checklist na tela inicial acompanha esses passos e some quando você termina.',
      'Nada é enviado a ninguém enquanto a escala está em rascunho.',
    ],
    telas: ['/inicio'],
    video: 'onboarding-da-igreja',
    busca: ['começar', 'primeiro acesso', 'configurar', 'checklist'],
  },
  {
    slug: 'importar-pessoas',
    area: 'pessoas',
    titulo: 'Como trago a minha planilha de voluntários?',
    resumo: 'Importa a lista que a igreja já tem, conferindo tudo antes de gravar.',
    passos: [
      'Abra Pessoas e toque em "Importar planilha".',
      'Cole o conteúdo da planilha ou envie o arquivo.',
      'Confira a prévia: o sistema mostra quem é novo, quem já existe e o que está com problema.',
      'Corrija o que precisar na sua planilha e cole de novo, ou importe assim mesmo — as linhas com problema ficam de fora.',
    ],
    dicas: [
      'Nada é gravado enquanto você não confirma. A prévia é só uma conferência.',
      'Se a mesma pessoa aparecer duas vezes, o sistema avisa qual linha repetiu.',
    ],
    telas: ['/pessoas', '/pessoas/importar'],
    video: 'importar-pessoas',
    busca: ['planilha', 'excel', 'csv', 'lista', 'voluntários'],
  },
  {
    slug: 'montar-a-escala',
    area: 'escalas',
    titulo: 'Como monto e publico a escala do mês?',
    resumo: 'Monta o mês inteiro e avisa todo mundo de uma vez.',
    passos: [
      'Abra Escalas e escolha o mês.',
      'Em cada vaga, escolha quem serve. O sistema avisa quando alguém está bloqueado, sobrecarregado ou já escalado em outro lugar no mesmo horário.',
      'Se quiser ajuda, use a auto-escala: ela sugere e explica por que sugeriu cada pessoa.',
      'Quando estiver pronta, toque em "Publicar". É aí que quem foi escalado recebe o aviso.',
    ],
    dicas: [
      'Enquanto está em rascunho, ninguém recebe nada — você pode montar com calma.',
      'Vaga essencial em vermelho é a que não pode ficar vazia no domingo.',
    ],
    telas: ['/escalas'],
    video: 'montar-e-publicar-a-escala',
    busca: ['publicar', 'escalar', 'mês', 'auto-escala', 'sugestão'],
  },
  {
    slug: 'confirmar-que-vou-servir',
    area: 'escalas',
    titulo: 'Fui escalado. Como confirmo?',
    resumo: 'Confirma em um toque, e a liderança sabe que pode contar com você.',
    passos: [
      'Abra "Minhas escalas" ou toque no aviso que você recebeu.',
      'Toque em "Confirmo" na escala. Pronto.',
      'Se não puder, toque em "Não vou poder" e diga o motivo — a liderança procura alguém no seu lugar.',
    ],
    dicas: [
      'Dá para confirmar pelo link do WhatsApp, sem abrir o aplicativo e sem senha.',
      'Sem internet, o toque fica guardado e é enviado quando a conexão voltar.',
    ],
    telas: ['/minhas-escalas'],
    video: 'confirmar-presenca',
    busca: ['confirmar', 'aceitar', 'recusar', 'não vou poder'],
  },
  {
    slug: 'pedir-troca',
    area: 'escalas',
    titulo: 'Não vou poder servir. Como peço troca?',
    resumo: 'Pede a troca sem sumir e sem deixar o domingo descoberto.',
    passos: [
      'Em "Minhas escalas", abra a escala e toque em "Pedir troca".',
      'Escolha alguém que faça a mesma função, ou deixe o sistema convidar quem está livre.',
      'Quem aceitar assume a vaga, e a liderança aprova se a igreja exigir aprovação.',
    ],
    dicas: ['Pedir troca cedo é o que evita buraco na escala. Ninguém se incomoda.'],
    telas: ['/minhas-escalas'],
    video: 'pedir-troca',
    busca: ['troca', 'substituto', 'trocar', 'não posso'],
  },
  {
    slug: 'conectar-o-whatsapp',
    area: 'comunicacao',
    titulo: 'Como ligo o WhatsApp da igreja?',
    resumo: 'Conecta o número da igreja para os avisos chegarem onde as pessoas já estão.',
    passos: [
      'Abra Configurações › WhatsApp.',
      'Toque em "Conectar" e leia o QR code com o celular da igreja, em WhatsApp › Aparelhos conectados.',
      'Quando o status ficar "Conectado", envie a mensagem de teste.',
    ],
    dicas: [
      'Use o número da igreja, e não o pessoal de alguém: quem sair da liderança leva o número junto.',
      'Se a sessão cair, a liderança recebe um aviso e é só ler o QR de novo.',
    ],
    telas: ['/configuracoes/whatsapp'],
    video: 'conectar-o-whatsapp',
    busca: ['whatsapp', 'qr', 'conectar', 'zap'],
  },
  {
    slug: 'montar-o-roteiro',
    area: 'culto',
    titulo: 'Como monto o roteiro do culto?',
    resumo: 'Monta a ordem do culto e conduz do celular, com todo mundo vendo o mesmo.',
    passos: [
      'Abra o culto na agenda e vá em "Roteiro".',
      'Adicione os blocos na ordem: louvor, avisos, palavra, ministração.',
      'Arraste para reordenar e ajuste os minutos de cada bloco.',
      'No domingo, toque em "Apresentar": quem está na equipe vê o mesmo bloco que você, ao vivo.',
    ],
    telas: ['/agenda/*/roteiro', '/agenda/*/apresentacao'],
    video: 'montar-o-roteiro',
    busca: ['roteiro', 'ordem do culto', 'apresentação', 'ao vivo'],
  },
  {
    slug: 'check-in-do-kids',
    area: 'kids',
    titulo: 'Como faço o check-in na porta do infantil?',
    resumo: 'Entrega a criança na sala em vinte segundos, com etiqueta e código de retirada.',
    passos: [
      'Abra a estação do infantil no tablet da porta.',
      'Digite o fim do telefone do responsável: a família inteira aparece junto.',
      'Toque nas crianças que estão chegando e confirme quem está entregando.',
      'Imprima a etiqueta. O código de retirada sai nela e no celular do responsável.',
    ],
    dicas: [
      'A estação funciona sem internet: os check-ins ficam na fila e sobem sozinhos quando a conexão volta.',
      'A etiqueta traz o selo de alerta quando a criança tem alguma restrição — mesmo para quem não pode abrir a ficha.',
    ],
    telas: ['/kids/estacao'],
    video: 'check-in-do-kids',
    busca: ['check-in', 'entrada', 'etiqueta', 'porta', 'infantil'],
  },
  {
    slug: 'retirada-sem-autorizacao',
    area: 'kids',
    titulo: 'Alguém sem autorização veio buscar a criança. O que faço?',
    resumo: 'O que fazer quando quem chega não está na lista de quem pode retirar.',
    passos: [
      'Não entregue a criança. A lista de quem pode retirar é a regra, e ela existe para proteger.',
      'Chame o responsável pelo botão "Chamar o responsável" — ele recebe na hora.',
      'Se o responsável autorizar e não puder vir, a liderança do infantil pode liberar a retirada excepcional, com o motivo escrito.',
      'A liberação fica registrada com quem autorizou, quem retirou e por quê.',
    ],
    dicas: [
      'Constrangimento de um minuto vale menos que o risco. Ninguém que trabalha em infantil se arrepende de ter conferido.',
    ],
    telas: ['/kids/estacao'],
    video: 'retirada-excepcional',
    busca: ['retirada', 'buscar', 'autorizado', 'excepcional', 'segurança'],
  },
  {
    slug: 'ler-os-relatorios',
    area: 'relatorios',
    titulo: 'O que os relatórios me dizem?',
    resumo: 'Entende os números e leva o que precisa em PDF ou planilha.',
    passos: [
      'Abra Relatórios e escolha o período.',
      'Veja a cobertura (quantos lugares da escala estão preenchidos) e o que ainda falta culto a culto.',
      'Olhe a carga por pessoa: quem está servindo demais e quem nunca é chamado.',
      'Escolha o relatório e baixe em PDF, para imprimir, ou em planilha, para somar e filtrar.',
    ],
    telas: ['/relatorios'],
    video: 'ler-os-relatorios',
    busca: ['relatório', 'pdf', 'planilha', 'exportar', 'cobertura'],
  },
  {
    slug: 'assinar-e-trocar-de-plano',
    area: 'conta',
    titulo: 'Como assino, troco de plano ou vejo as faturas?',
    resumo: 'Cuida da assinatura da igreja sem depender de ninguém.',
    passos: [
      'Abra Assinatura.',
      'Compare os planos e escolha mensal ou anual — o anual custa dez meses.',
      'Toque no plano que quiser: a cobrança é gerada e o link de pagamento abre.',
      'As faturas ficam listadas ali, com segunda via quando precisar.',
    ],
    dicas: [
      'Se a avaliação terminar, nada é apagado: a igreja continua vendo e exportando tudo, e para de criar e publicar até assinar.',
      'O pagamento acontece no ambiente do Asaas. O Levitas nunca guarda dado de cartão.',
    ],
    telas: ['/assinatura'],
    video: 'assinar-e-trocar-de-plano',
    busca: ['plano', 'pagar', 'fatura', 'boleto', 'pix', 'cobrança'],
  },
  {
    slug: 'verificacao-em-duas-etapas',
    area: 'conta',
    titulo: 'Como protejo minha conta com o celular?',
    resumo:
      'Liga a verificação em duas etapas: nem quem descobrir sua senha entra sem o seu celular.',
    passos: [
      'Instale um aplicativo autenticador no celular — Google Authenticator, Authy ou o gerenciador de senhas que você já usa.',
      'Abra Segurança da conta e toque em "Ligar".',
      'Leia o código QR com o aplicativo. Se o celular não ler, digite a chave que aparece ao lado.',
      'Digite no Levitas o número de seis dígitos que o aplicativo mostrar e confirme.',
      'Guarde os oito códigos de recuperação que aparecem. Eles só aparecem uma vez.',
    ],
    dicas: [
      'Quem mexe na configuração da igreja tem, na prática, a chave dela. É para essas contas que o produto cobra a verificação.',
      'Perdeu o celular? Use um código de recuperação no lugar dos seis dígitos, na própria tela de entrar. Cada um funciona uma vez só.',
      'Acabaram os códigos de recuperação? Na tela de Segurança dá para gerar oito novos — os antigos param de valer.',
      'O código não confere? Quase sempre é o relógio do celular fora de hora. Ligue o horário automático e tente de novo.',
    ],
    telas: ['/configuracoes/seguranca'],
    busca: ['2fa', 'duas etapas', 'autenticador', 'código', 'segurança', 'senha', 'proteger'],
  },
  {
    slug: 'dados-da-igreja',
    area: 'conta',
    titulo: 'Onde mudo o nome, a cor e as regras da igreja?',
    resumo: 'Ajusta o que aparece nos avisos e as regras que valem para todo mundo.',
    passos: [
      'Abra Dados da igreja.',
      'Ajuste nome, denominação, cor da marca e fuso horário.',
      'Escolha com quantas horas de antecedência o Levitas cobra a confirmação de quem foi escalado.',
      'Escolha por quanto tempo a igreja guarda o que comunicou.',
      'Toque em Salvar.',
    ],
    dicas: [
      'Passado o prazo de guarda, os avisos, as entregas e as mensagens de WhatsApp são apagados de verdade. É o que a LGPD pede: dado pessoal não se guarda para sempre.',
      'A trilha de auditoria e os registros do Ministério Infantil não entram nessa conta — eles são prova, e têm prazo próprio.',
      'Só quem cuida da configuração da igreja consegue alterar. As outras pessoas veem, e não mexem.',
    ],
    telas: ['/configuracoes/igreja'],
    busca: ['configuração', 'nome da igreja', 'cor', 'marca', 'fuso', 'retenção', 'lgpd', 'prazo'],
  },
  {
    slug: 'modo-simplificado',
    area: 'conta',
    titulo: 'A letra está pequena. Dá para aumentar?',
    resumo: 'Deixa o Levitas maior, mais contrastado e com menos coisa na tela.',
    passos: [
      'Abra o menu da sua conta e ligue o "Modo simplificado".',
      'A letra fica maior, o contraste aumenta e as telas mostram só o essencial.',
      'A escolha é sua e vale em qualquer aparelho onde você entrar.',
    ],
    dicas: ['Você pode desligar a qualquer momento, no mesmo lugar.'],
    telas: ['/configuracoes/acessibilidade'],
    busca: ['letra', 'fonte', 'grande', 'enxergar', 'contraste', 'simples'],
  },
  {
    slug: 'criar-culto-recorrente',
    area: 'escalas',
    titulo: 'O culto é todo domingo. Preciso cadastrar um por um?',
    resumo: 'Cria o culto que se repete e deixa a agenda do semestre pronta de uma vez.',
    passos: [
      'Abra a Agenda e toque em "Novo evento".',
      'Escolha o tipo, o título, a primeira data e a hora de começar.',
      'Marque os dias da semana em que ele se repete e por quantos meses.',
      'Diga quantas vagas cada função tem naquele culto.',
      'Toque em "Criar agenda": todas as ocorrências aparecem de uma vez.',
    ],
    dicas: [
      'Um culto cancelado se apaga sozinho, sem mexer nos outros da série.',
      'As vagas que você definir aqui são as linhas que vão aparecer na escala.',
    ],
    telas: ['/agenda'],
    video: 'criar-culto-recorrente',
    busca: ['repetir', 'toda semana', 'recorrente', 'evento', 'domingo'],
  },
  {
    slug: 'usar-a-auto-escala',
    area: 'escalas',
    titulo: 'Dá para o sistema sugerir quem escalar?',
    resumo: 'Preenche o mês em segundos e explica, pessoa por pessoa, por que sugeriu.',
    passos: [
      'Na tela de Escalas, escolha o mês e toque em "Sugerir escala".',
      'Confira a lista: cada sugestão vem com o motivo — quem serviu menos, quem está livre, quem sabe fazer.',
      'Desmarque o que você não quiser e ajuste à mão o que preferir.',
      'Toque em aceitar. As sugestões viram rascunho, e nada foi avisado ainda.',
    ],
    dicas: [
      'A sugestão nunca escala quem bloqueou a data nem quem já está em outro lugar no mesmo horário.',
      'Vaga que ficou sem ninguém aparece destacada: é onde falta gente habilitada.',
    ],
    telas: ['/escalas'],
    video: 'usar-a-auto-escala',
    busca: ['sugestao', 'automatico', 'sortear', 'preencher'],
  },
  {
    slug: 'check-in-no-dia',
    area: 'escalas',
    titulo: 'Como sei quem chegou para servir hoje?',
    resumo: 'Acompanha a chegada da equipe ao vivo, com um código que a recepção mostra.',
    passos: [
      'Abra o culto de hoje na agenda e vá em "Painel do dia".',
      'Toque em "Mostrar código": ele aparece grande, para a equipe ver ao chegar.',
      'Quem serve abre "Minhas escalas", toca em registrar chegada e digita o código.',
      'O painel marca a pessoa como presente na hora, sem você atualizar a tela.',
    ],
    dicas: [
      'O código muda sozinho de tempos em tempos: quem não veio não consegue registrar de casa.',
      'O painel mostra quem confirmou, quem chegou e quem ainda falta.',
    ],
    telas: ['/agenda/*/dia', '/checkin'],
    video: 'check-in-no-dia',
    busca: ['presenca', 'chegada', 'codigo', 'painel do dia'],
  },
  {
    slug: 'enviar-comunicado',
    area: 'comunicacao',
    titulo: 'Como aviso um departamento inteiro?',
    resumo: 'Manda o recado para exatamente quem precisa receber, e vê quantos foram alcançados.',
    passos: [
      'Abra Comunicados e toque em "Novo comunicado".',
      'Escreva o título e a mensagem, em linguagem de gente.',
      'Escolha quem recebe: a igreja toda, um departamento ou uma função.',
      'Confira quantas pessoas serão alcançadas e envie.',
    ],
    dicas: [
      'Quem pediu para não receber pelo WhatsApp fica de fora automaticamente.',
      'O comunicado também aparece no sino de quem usa o aplicativo.',
    ],
    telas: ['/comunicados'],
    video: 'enviar-comunicado',
    busca: ['aviso', 'recado', 'mensagem', 'departamento'],
  },
  {
    slug: 'repertorio-e-setlist',
    area: 'culto',
    titulo: 'Como monto o repertório e o tom de cada música?',
    resumo: 'Guarda a cifra uma vez e transpõe para o tom daquele domingo, sem refazer nada.',
    passos: [
      'Abra Repertório e toque em "Nova música": título, autor, tom base e andamento.',
      'Entre na música e escreva a cifra e a letra.',
      'Escolha outro tom na própria tela para ver a cifra transposta.',
      'No roteiro do culto, inclua a música no item de louvor e diga o tom daquele domingo.',
    ],
    dicas: [
      'A letra nunca é confundida com acorde: só a linha de cifra é transposta.',
      'O Levitas avisa quando a mesma música se repete perto demais.',
    ],
    telas: ['/repertorio'],
    video: 'repertorio-e-setlist',
    busca: ['musica', 'cifra', 'tom', 'transpor', 'setlist', 'louvor'],
  },
  {
    slug: 'gerenciar-permissoes',
    area: 'pessoas',
    titulo: 'Como digo o que cada pessoa pode fazer?',
    resumo: 'Dá acesso ao aplicativo e define quem lidera o quê, sem abrir a igreja inteira para todo mundo.',
    passos: [
      'Na ficha da pessoa, toque em "Convidar para o app": ela recebe um link para criar a senha.',
      'Em Equipes, inclua a pessoa no departamento e escolha o papel: membro, auxiliar ou líder.',
      'Ainda na equipe, marque as funções que ela sabe fazer — é o que a habilita a ser escalada.',
      'Líder de departamento enxerga e monta a escala do departamento dele, e só dele.',
    ],
    dicas: [
      'Ninguém vê a ficha de saúde de criança sem permissão explícita, nem o administrador.',
      'Tirar a pessoa do departamento tira o acesso junto, na hora.',
    ],
    telas: ['/departamentos', '/pessoas/*'],
    video: 'gerenciar-permissoes',
    busca: ['permissao', 'acesso', 'lider', 'papel', 'convidar'],
  },
  {
    slug: 'cadastrar-crianca',
    area: 'kids',
    titulo: 'Como cadastro uma criança e os responsáveis?',
    resumo: 'Deixa a família pronta para o check-in de domingo, com quem pode retirar já definido.',
    passos: [
      'Abra Infantil e toque em cadastrar criança.',
      'Preencha o nome, como a criança é chamada e a data de nascimento — é ela que define a sala.',
      'Cadastre o responsável principal e, se houver, o segundo.',
      'Na ficha da criança, autorize quem mais pode retirar: avó, tio, motorista da van.',
    ],
    dicas: [
      'Só quem está autorizado aparece na conferência da saída. É essa lista que protege a criança.',
      'O consentimento dos dados é pedido no cadastro: sem ele, o Levitas não usa imagem nem guarda saúde.',
    ],
    telas: ['/kids'],
    video: 'cadastrar-crianca',
    busca: ['crianca', 'responsavel', 'matricula', 'familia'],
  },
  {
    slug: 'ficha-de-saude',
    area: 'kids',
    titulo: 'Onde registro alergia e medicamento?',
    resumo: 'Guarda o que a equipe precisa saber numa emergência, sem espalhar isso pelo sistema.',
    passos: [
      'Abra a criança e vá na aba "Ficha de saúde".',
      'Registre alergias, com a gravidade e o que fazer em caso de reação.',
      'Anote condições, medicamentos de uso contínuo e o contato de emergência.',
      'Salve: o alerta grave passa a aparecer no cartão, na etiqueta e na tela da sala.',
    ],
    dicas: [
      'A ficha é cifrada e só é aberta por quem tem permissão — e todo acesso fica registrado.',
      'A etiqueta mostra só o alerta que salva a criança, e nunca o diagnóstico dela.',
    ],
    telas: ['/kids/*'],
    video: 'ficha-de-saude',
    busca: ['alergia', 'saude', 'remedio', 'medicamento', 'emergencia'],
  },
  {
    slug: 'salas-e-proporcao',
    area: 'kids',
    titulo: 'Como organizo as salas por idade?',
    resumo: 'Monta as salas do infantil e mantém a conta de adultos por criança sob controle.',
    passos: [
      'Em Infantil, abra "Salas" e toque em criar as salas de sempre.',
      'Ajuste a faixa de idade e a capacidade de cada sala.',
      'Defina quantas crianças cada adulto pode acompanhar naquela sala.',
      'No domingo, a tela da sala avisa quando a proporção estoura.',
    ],
    dicas: [
      'A criança vai para a sala pela data de nascimento: ninguém precisa escolher na porta.',
      'Sala cheia aparece destacada antes de virar problema.',
    ],
    telas: ['/kids/salas', '/kids/sala/*'],
    video: 'salas-e-proporcao',
    busca: ['sala', 'idade', 'proporcao', 'capacidade', 'turma'],
  },
  {
    slug: 'imprimir-etiquetas',
    area: 'kids',
    titulo: 'Como imprimo as etiquetas do check-in?',
    resumo: 'Tira as três etiquetas de cada criança na impressora térmica da porta.',
    passos: [
      'Faça o check-in normalmente: as etiquetas aparecem prontas na tela.',
      'Toque em imprimir. Saem três: a da criança, a da mochila e a de quem trouxe.',
      'Confira se o código da etiqueta da mochila é o mesmo da etiqueta do responsável.',
      'Entregue a etiqueta do responsável a quem vai buscar a criança.',
    ],
    dicas: [
      'Sem impressora, dá para anotar o código à mão: ele é curto de propósito.',
      'Cada criança tem o próprio código, mesmo entre irmãos.',
    ],
    telas: ['/kids/estacao'],
    video: 'imprimir-etiquetas',
    busca: ['etiqueta', 'imprimir', 'impressora', 'pulseira'],
  },
  {
    slug: 'check-out-com-seguranca',
    area: 'kids',
    titulo: 'Como entrego a criança com segurança?',
    resumo: 'Confere o código e a pessoa autorizada antes de liberar — as duas coisas, sempre.',
    passos: [
      'Na porta do infantil, abra a aba "Saída".',
      'Digite o código que está na etiqueta de quem veio buscar.',
      'Confira a foto e o nome: o Levitas mostra quem está autorizado a retirar aquela criança.',
      'Toque em entregar para a pessoa certa. A saída fica registrada com a hora e com quem entregou.',
    ],
    dicas: [
      'Código certo com pessoa não autorizada não libera. Nem o contrário.',
      'Se quem veio buscar não está na lista, chame a liderança: existe a retirada excepcional.',
    ],
    telas: ['/kids/estacao'],
    video: 'check-out-com-seguranca',
    busca: ['saida', 'retirada', 'entregar', 'buscar'],
  },
  {
    slug: 'registrar-ocorrencia',
    area: 'kids',
    titulo: 'A criança se machucou. O que eu registro?',
    resumo: 'Registra o que houve no diário da criança e avisa o responsável na hora.',
    passos: [
      'Na tela da sala, encontre a criança e toque em registrar.',
      'Diga o que aconteceu, a gravidade e o que a equipe fez.',
      'Salve: o responsável recebe o aviso e o registro fica no diário da criança.',
      'Se for grave e ninguém confirmar a leitura, o Levitas avisa o segundo responsável e a liderança.',
    ],
    dicas: [
      'Escreva o que aconteceu de verdade: é esse texto que protege a igreja e a família depois.',
      'O registro não pode ser apagado — nem por quem escreveu.',
    ],
    telas: ['/kids/sala/*'],
    video: 'registrar-ocorrencia',
    busca: ['ocorrencia', 'machucou', 'acidente', 'diario', 'queda'],
  },
  {
    slug: 'chamar-o-responsavel',
    area: 'kids',
    titulo: 'Como chamo o pai ou a mãe durante o culto?',
    resumo: 'Chama o responsável sem interromper o culto nem procurar ninguém no corredor.',
    passos: [
      'Na tela da sala, ache a criança e toque em "Chamar".',
      'Diga o motivo: chorando, fralda, febre, ou outro.',
      'O responsável recebe na hora, no celular, com o nome da criança e a sala.',
      'A tela mostra os chamados em andamento até alguém chegar.',
    ],
    dicas: [
      'O chamado fica no diário do culto: ninguém precisa lembrar de cabeça depois.',
      'Se o responsável não confirmar a leitura, o aviso sobe para o segundo responsável.',
    ],
    telas: ['/kids/sala/*'],
    video: 'chamar-o-responsavel',
    busca: ['chamar', 'chorando', 'fralda', 'pai', 'mae'],
  },
  {
    slug: 'app-do-responsavel',
    area: 'kids',
    titulo: 'Sou pai. O que eu vejo no Levitas?',
    resumo: 'Acompanha o filho no culto: o código de retirada, os avisos e como foi o domingo.',
    passos: [
      'Entre no Levitas e abra "Meus filhos".',
      'Veja o código de retirada de cada criança — é ele que a recepção pede na saída.',
      'Receba os chamados e as ocorrências no celular, e confirme que leu.',
      'Toque em "Ver como foram os cultos" para o histórico da criança.',
    ],
    dicas: [
      'Você vê só os seus filhos, e ninguém mais vê os dados deles sem permissão.',
      'Na mesma tela dá para exportar ou pedir a exclusão dos dados da criança.',
    ],
    telas: ['/kids/meus-filhos'],
    video: 'app-do-responsavel',
    busca: ['pai', 'mae', 'responsavel', 'meus filhos', 'codigo'],
  },
] as const;

export const artigoPorSlug = (slug: string): ArtigoDeAjuda | undefined =>
  ARTIGOS_DE_AJUDA.find((artigo) => artigo.slug === slug);

/**
 * O artigo que o botão de ajuda daquela tela deve abrir.
 *
 * Casa pelo caminho mais específico primeiro: `/pessoas/importar` prefere o
 * artigo da importação ao artigo geral de pessoas.
 */
export function ajudaDaTela(caminho: string): ArtigoDeAjuda | undefined {
  const candidatos = ARTIGOS_DE_AJUDA.filter((artigo) =>
    artigo.telas.some((tela) => combina(tela, caminho)),
  );

  return candidatos.sort((a, b) => maiorTela(b, caminho) - maiorTela(a, caminho))[0];
}

/**
 * A tela do artigo casa com o caminho da vez.
 *
 * O `*` vale por um trecho, e existe porque telas de um registro só têm o
 * identificador no meio: `/agenda/*` + `/roteiro` é o roteiro de **qualquer**
 * culto, e sem isso o artigo do roteiro teria de se contentar com `/agenda`,
 * roubando o botão de ajuda da própria agenda.
 */
function combina(tela: string, caminho: string): boolean {
  if (!tela.includes('*')) return caminho === tela || caminho.startsWith(`${tela}/`);

  const padrao = tela
    .split('*')
    .map((parte) => parte.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`))
    .join('[^/]+');

  return new RegExp(`^${padrao}(/|$)`).test(caminho);
}

function maiorTela(artigo: ArtigoDeAjuda, caminho: string): number {
  return Math.max(
    ...artigo.telas.filter((tela) => combina(tela, caminho)).map((tela) => tela.length),
  );
}

/** Busca por título, resumo, passos e pelos termos que a pessoa digitaria. */
export function buscarAjuda(termo: string): readonly ArtigoDeAjuda[] {
  const alvo = normalizar(termo);
  if (alvo.length < 2) return ARTIGOS_DE_AJUDA;

  return ARTIGOS_DE_AJUDA.filter((artigo) => {
    const palheiro = normalizar(
      [artigo.titulo, artigo.resumo, ...artigo.passos, ...(artigo.busca ?? [])].join(' '),
    );
    return alvo.split(/\s+/).every((palavra) => palheiro.includes(palavra));
  });
}

/** Sem acento e em minúsculas: quem digita "criancas" acha "crianças". */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

// ── Tour guiado ──────────────────────────────────────────────────────────────

export interface PassoDoTour {
  /** O elemento destacado, por `data-testid`. Sem ele, o passo é uma cartela. */
  alvo?: string;
  titulo: string;
  texto: string;
}

export interface Tour {
  area: AreaDeAjuda;
  /** A tela em que este tour roda na primeira visita. */
  tela: string;
  passos: readonly PassoDoTour[];
}

/**
 * O tour da primeira visita de cada área (M18).
 *
 * Curto de propósito: três ou quatro passos. Tour longo é pulado, e tour pulado
 * não ensina ninguém.
 */
export const TOURS: readonly Tour[] = [
  {
    area: 'primeiros-passos',
    tela: '/inicio',
    passos: [
      {
        titulo: 'Bem-vindo ao Levitas',
        texto:
          'Em três telas você monta a escala do mês inteiro. Vamos mostrar onde fica cada coisa — leva meio minuto.',
      },
      {
        alvo: 'checklist-de-ativacao',
        titulo: 'Comece por aqui',
        texto:
          'Este checklist acompanha o que falta para a sua igreja ficar pronta. Ele some sozinho quando você termina.',
      },
      {
        alvo: 'nav-lateral',
        titulo: 'Tudo mora neste menu',
        texto:
          'Pessoas, escalas, agenda e relatórios. No celular, ele fica na barra de baixo.',
      },
      {
        alvo: 'botao-de-ajuda',
        titulo: 'A ajuda acompanha a tela',
        texto:
          'Este botão abre a explicação da tela em que você está — com o passo a passo e, quando existe, o vídeo.',
      },
    ],
  },
  {
    area: 'escalas',
    tela: '/escalas',
    passos: [
      {
        titulo: 'A escala do mês',
        texto: 'Aqui você preenche as vagas de cada culto e publica tudo de uma vez.',
      },
      {
        alvo: 'grade-de-escalas',
        titulo: 'Cada coluna é um culto',
        texto:
          'Toque numa vaga para escolher quem serve. O sistema avisa quando alguém está bloqueado ou já escalado no mesmo horário.',
      },
      {
        alvo: 'abrir-publicacao',
        titulo: 'Publicar é avisar',
        texto:
          'Enquanto está em rascunho, ninguém recebe nada. A publicação é o que convoca quem foi escalado.',
      },
    ],
  },
  {
    area: 'kids',
    tela: '/kids/estacao',
    passos: [
      {
        titulo: 'A porta do infantil',
        texto:
          'Esta tela é feita para a correria da entrada: poucos toques, letra grande e funciona sem internet.',
      },
      {
        alvo: 'busca-da-familia',
        titulo: 'Busque pelo fim do telefone',
        texto: 'A família inteira aparece junto, e você faz o check-in dos irmãos numa operação só.',
      },
      {
        alvo: 'fila-pendente',
        titulo: 'Sem internet, nada se perde',
        texto:
          'Os check-ins ficam nesta fila e sobem sozinhos quando a conexão volta. Pode continuar trabalhando.',
      },
    ],
  },
];

export const tourDaTela = (caminho: string): Tour | undefined =>
  TOURS.find((tour) => tour.tela === caminho);

// ── Preferências de acessibilidade ───────────────────────────────────────────

export const preferenciasDeAcessibilidadeSchema = z.object({
  /**
   * Fonte maior, contraste reforçado e menos opções por tela (seção 9.5).
   *
   * É por pessoa, e não por aparelho: quem precisa dele precisa em todo lugar
   * onde entrar.
   */
  modoSimplificado: z.boolean(),
});
export type PreferenciasDeAcessibilidade = z.infer<typeof preferenciasDeAcessibilidadeSchema>;
